// Folder Templates admin — author reusable folder trees, link to a project template
// for auto-apply, and apply on-demand to a project. Director/administrator only.
import { useEffect, useMemo, useState } from "react";
import {
    Plus, Pencil, Trash2, X, Save, Loader2, Star, FolderPlus, FolderOpen,
    ChevronRight, EyeOff, Eye, CornerDownRight, Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["director", "administrator"]);
const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";
const uid = () => (crypto.randomUUID ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10));
const MAX_DEPTH = 4;

export default function FolderTemplatesPage() {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [projectTemplates, setProjectTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);
    const [applyFor, setApplyFor] = useState(null);
    const isAdmin = user && ADMIN_ROLES.has(user.role);

    function load() {
        setLoading(true);
        Promise.all([
            api.get("/folder-templates").then((r) => r.data),
            api.get("/project-templates").then((r) => r.data).catch(() => []),
        ]).then(([ft, pt]) => { setTemplates(ft); setProjectTemplates(pt); })
            .catch(() => toast.error("Could not load folder templates"))
            .finally(() => setLoading(false));
    }
    useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

    async function remove(t) {
        if (!window.confirm(`Delete folder template "${t.name}"?`)) return;
        try { await api.delete(`/folder-templates/${t.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }

    if (!isAdmin) return <div data-testid="folder-templates-page" className="py-12 text-center text-[#9A968F]">Admin-only section.</div>;

    const ptName = (id) => projectTemplates.find((p) => p.id === id)?.name;

    return (
        <div data-testid="folder-templates-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent,#C75A1B)", color: "#6F6A64" }}>Admin · Templates</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Folder Templates</h2>
                    <p className="text-[13px] mt-1 text-[#6F6A64]">Reusable folder trees applied to a project's Documents area. Link one to a project template to auto-build folders on project creation.</p>
                </div>
                <button data-testid="folder-template-new" onClick={() => setDialog({ mode: "create" })}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                    <Plus size={15} /> New folder template
                </button>
            </div>

            {loading ? (
                <div className="py-16 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>
            ) : templates.length === 0 ? (
                <div className="py-16 text-center text-[#9A968F] border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>No folder templates yet. Create your first reusable tree.</div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((t) => (
                        <div key={t.id} data-testid={`folder-template-card-${t.id}`} className="bg-white border rounded-[12px] p-4 shadow-brand-sm flex flex-col" style={{ borderColor: "#E5E0DA" }}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <FolderOpen size={15} className="text-[#C68A1C]" />
                                        <h3 className="font-bold text-[#1F1F1F] truncate">{t.name}</h3>
                                        {t.is_default && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#FBEFE5] text-[#9F4212]"><Star size={9} /> Default</span>}
                                        {t.auto_apply_new_projects && <span data-testid={`folder-template-autoapply-badge-${t.id}`} className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#E7F1EA] text-[#2E7D5B]"><Send size={9} /> Auto</span>}
                                    </div>
                                    {t.description && <p className="text-[12px] text-[#6F6A64] mt-1 line-clamp-2">{t.description}</p>}
                                </div>
                            </div>
                            <div className="text-[11.5px] text-[#6F6A64] mt-2 space-y-0.5">
                                <div>{t.nodes?.length || 0} folders</div>
                                {t.linked_project_template_id && <div className="text-[#9F4212]">Auto-applies: {ptName(t.linked_project_template_id) || "linked template"}</div>}
                                {t.auto_apply_new_projects && <div className="text-[#2E7D5B]">Auto-applies to every new project</div>}
                            </div>
                            <div className="mt-3 pt-3 border-t flex items-center gap-2" style={{ borderColor: "#F0EDE8" }}>
                                <button data-testid={`folder-template-apply-${t.id}`} onClick={() => setApplyFor(t)} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-white" style={{ background: "#2A5DB0" }}><Send size={12} /> Apply</button>
                                <button data-testid={`folder-template-edit-${t.id}`} onClick={() => setDialog({ mode: "edit", t })} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}><Pencil size={12} /> Edit</button>
                                <button data-testid={`folder-template-delete-${t.id}`} onClick={() => remove(t)} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-[#B33A2E] hover:bg-[#FBE8E5] ml-auto"><Trash2 size={12} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {dialog && <TemplateDialog mode={dialog.mode} initial={dialog.t} projectTemplates={projectTemplates}
                onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
            {applyFor && <ApplyDialog template={applyFor} onClose={() => setApplyFor(null)} />}
        </div>
    );
}

function TemplateDialog({ mode, initial, projectTemplates, onClose, onSaved }) {
    const [form, setForm] = useState(() => ({
        name: initial?.name || "",
        description: initial?.description || "",
        is_default: initial?.is_default || false,
        auto_apply_new_projects: initial?.auto_apply_new_projects || false,
        linked_project_template_id: initial?.linked_project_template_id || "",
        nodes: (initial?.nodes || []).map((n) => ({ ...n })),
    }));
    const [busy, setBusy] = useState(false);

    const childrenOf = useMemo(() => {
        const m = {};
        form.nodes.forEach((n) => { (m[n.parent_temp_id || "__root__"] ||= []).push(n); });
        Object.values(m).forEach((a) => a.sort((x, y) => (x.sort_order - y.sort_order)));
        return m;
    }, [form.nodes]);

    const depthOf = (n) => {
        let d = 0, cur = n, guard = 0;
        const byId = Object.fromEntries(form.nodes.map((x) => [x.temp_id, x]));
        while (cur && cur.parent_temp_id && guard < 8) { cur = byId[cur.parent_temp_id]; d++; guard++; }
        return d;
    };

    const flat = useMemo(() => {
        const out = [];
        const walk = (pid, depth) => (childrenOf[pid] || []).forEach((n) => { out.push({ n, depth }); walk(n.temp_id, depth + 1); });
        walk("__root__", 0);
        return out;
    }, [childrenOf]);

    function addNode(parent_temp_id) {
        const siblings = (childrenOf[parent_temp_id || "__root__"] || []);
        setForm((f) => ({ ...f, nodes: [...f.nodes, { temp_id: uid(), parent_temp_id: parent_temp_id || null, name: "New folder", portal_visible: true, sort_order: siblings.length }] }));
    }
    function patchNode(temp_id, patch) {
        setForm((f) => ({ ...f, nodes: f.nodes.map((n) => n.temp_id === temp_id ? { ...n, ...patch } : n) }));
    }
    function deleteNode(temp_id) {
        // remove node + all descendants
        const toRemove = new Set([temp_id]);
        let changed = true;
        while (changed) {
            changed = false;
            form.nodes.forEach((n) => { if (n.parent_temp_id && toRemove.has(n.parent_temp_id) && !toRemove.has(n.temp_id)) { toRemove.add(n.temp_id); changed = true; } });
        }
        setForm((f) => ({ ...f, nodes: f.nodes.filter((n) => !toRemove.has(n.temp_id)) }));
    }

    async function save() {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (form.nodes.some((n) => !n.name.trim())) { toast.error("Every folder needs a name"); return; }
        setBusy(true);
        try {
            const body = {
                name: form.name.trim(), description: form.description, is_default: form.is_default,
                auto_apply_new_projects: form.auto_apply_new_projects,
                linked_project_template_id: form.linked_project_template_id || null,
                nodes: form.nodes.map((n) => ({ temp_id: n.temp_id, parent_temp_id: n.parent_temp_id || null, name: n.name.trim(), portal_visible: n.portal_visible, sort_order: n.sort_order || 0 })),
            };
            if (mode === "create") await api.post("/folder-templates", body);
            else await api.patch(`/folder-templates/${initial.id}`, body);
            toast.success(mode === "create" ? "Template created" : "Template saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div data-testid="folder-template-dialog" className="bg-white rounded-[14px] shadow-xl w-full max-w-[720px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-base font-bold text-[#1F1F1F]">{mode === "create" ? "New folder template" : "Edit folder template"}</h3>
                    <button data-testid="folder-template-dialog-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>
                <div className="p-5 overflow-y-auto space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Name
                            <input data-testid="folder-template-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. H&S Project Standard" className={inputCls + " mt-1 font-normal normal-case"} /></label>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Auto-apply with project template
                            <select data-testid="folder-template-linked" value={form.linked_project_template_id} onChange={(e) => setForm((f) => ({ ...f, linked_project_template_id: e.target.value }))} className={inputCls + " mt-1 font-normal normal-case"}>
                                <option value="">— None (manual apply only) —</option>
                                {projectTemplates.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select></label>
                    </div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Description
                        <input data-testid="folder-template-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional" className={inputCls + " mt-1 font-normal normal-case"} /></label>
                    <label className="flex items-center gap-2 text-[12.5px] text-[#1F1F1F]">
                        <input data-testid="folder-template-default" type="checkbox" checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} /> Set as default (applied to projects with no linked template)
                    </label>
                    <label className="flex items-center gap-2 text-[12.5px] text-[#1F1F1F]">
                        <input data-testid="folder-template-autoapply" type="checkbox" checked={form.auto_apply_new_projects} onChange={(e) => setForm((f) => ({ ...f, auto_apply_new_projects: e.target.checked }))} /> Auto-apply to every new project (on creation)
                    </label>

                    <div className="border rounded-[10px] p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-[12px] font-bold text-[#1F1F1F]">Folder tree</h4>
                            <button data-testid="folder-template-add-root" type="button" onClick={() => addNode(null)} className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded-md border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}><FolderPlus size={13} /> Add top-level folder</button>
                        </div>
                        {flat.length === 0 ? (
                            <p className="text-[12px] italic text-[#9A968F] py-3 text-center">No folders yet — add a top-level folder to begin.</p>
                        ) : (
                            <div className="space-y-1">
                                {flat.map(({ n, depth }) => (
                                    <div key={n.temp_id} data-testid={`folder-template-node-${n.temp_id}`} className="flex items-center gap-2" style={{ paddingLeft: `${depth * 18}px` }}>
                                        {depth > 0 && <CornerDownRight size={13} className="text-[#C8C2B8] shrink-0" />}
                                        <FolderOpen size={14} className="text-[#C68A1C] shrink-0" />
                                        <input value={n.name} onChange={(e) => patchNode(n.temp_id, { name: e.target.value })} className="h-8 px-2 rounded-md border border-[#E5E0DA] bg-white text-[13px] flex-1 min-w-0" />
                                        <button type="button" title={n.portal_visible ? "Visible in portal" : "Hidden from portal"} onClick={() => patchNode(n.temp_id, { portal_visible: !n.portal_visible })}
                                            className={`p-1.5 rounded-md ${n.portal_visible ? "text-[#3F7D3A] hover:bg-green-50" : "text-[#9A968F] hover:bg-[#F0EDE8]"}`}>
                                            {n.portal_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                                        </button>
                                        {depth < MAX_DEPTH - 1 && (
                                            <button type="button" title="Add sub-folder" onClick={() => addNode(n.temp_id)} className="p-1.5 rounded-md text-[#6F6A64] hover:bg-[#F6F4F0]"><FolderPlus size={14} /></button>
                                        )}
                                        <button type="button" title="Delete" onClick={() => deleteNode(n.temp_id)} className="p-1.5 rounded-md text-[#B33A2E] hover:bg-red-50"><Trash2 size={14} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-[#F5F3F0]" style={{ borderColor: "#E5E0DA" }}>
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                    <button data-testid="folder-template-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {mode === "create" ? "Create" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ApplyDialog({ template, onClose }) {
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [clientId, setClientId] = useState("");
    const [projectId, setProjectId] = useState("");
    const [allProjects, setAllProjects] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => { }); }, []);
    useEffect(() => {
        if (!clientId) { setProjects([]); return; }
        api.get("/projects", { params: { client_id: clientId } }).then((r) => setProjects(r.data)).catch(() => { });
        setProjectId("");
    }, [clientId]);

    async function apply() {
        setBusy(true);
        try {
            if (allProjects) {
                const scope = clientId ? clients.find((c) => c.id === clientId)?.name : "all clients";
                const { data } = await api.post(`/folder-templates/${template.id}/apply-all`, clientId ? { client_id: clientId } : {});
                toast.success(`Applied to ${data.projects_applied} project(s) (${scope}) — ${data.folders_created} new folder(s)`);
                onClose();
            } else {
                if (!projectId) { toast.error("Pick a project"); setBusy(false); return; }
                const { data } = await api.post(`/folder-templates/${template.id}/apply`, { project_id: projectId });
                toast.success(`Applied — ${data.folders_created} new folder(s)`);
                onClose();
            }
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Apply failed"); }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div data-testid="folder-template-apply-dialog" className="bg-white rounded-[14px] shadow-xl w-full max-w-md p-5 border" style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-bold text-[#1F1F1F]">Apply "{template.name}"</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <p className="text-[12px] text-[#6F6A64] mb-3">Materialises the folder tree onto the chosen project. Re-applying is safe — existing folders are not duplicated.</p>

                <label className="flex items-center gap-2 text-[13px] font-semibold text-[#1F1F1F] mb-3 cursor-pointer">
                    <input type="checkbox" data-testid="apply-all-toggle" checked={allProjects}
                        onChange={(e) => setAllProjects(e.target.checked)} />
                    Apply to all projects{clientId ? " for the selected client" : ""}
                </label>

                <div className="space-y-3">
                    <select data-testid="apply-client" value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls}>
                        <option value="">{allProjects ? "— All clients —" : "— Select client —"}</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {!allProjects && (
                        <select data-testid="apply-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!clientId} className={inputCls + " disabled:bg-[#F6F4F0]"}>
                            <option value="">{clientId ? "— Select project —" : "Choose a client first"}</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.reference})</option>)}
                        </select>
                    )}
                    {allProjects && (
                        <p data-testid="apply-all-hint" className="text-[12px] text-[#9F4212] bg-[#FBEFE5] rounded-lg px-3 py-2">
                            This will materialise the tree onto {clientId ? "every project for the selected client" : "every project across all clients"}. Existing folders are skipped.
                        </p>
                    )}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                    <button data-testid="apply-confirm" onClick={apply} disabled={busy || (!allProjects && !projectId)} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: "#2A5DB0" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {allProjects ? "Apply to all" : "Apply"}
                    </button>
                </div>
            </div>
        </div>
    );
}
